import { fetchHackcal, findNextEvent } from "./events";
import { parseStart } from "./ics";
import { setVisibleInterval } from "./visible-interval";

const REFRESH_INTERVAL_MS = 60_000;
const TICK_INTERVAL_MS = 1_000;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function formatRemaining(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const clock = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return days > 0 ? `${days} Tag${days === 1 ? "" : "e"} ${clock}` : clock;
}

/**
 * Live countdown to the next Regeltermin/event, shown as a ticking hh:mm:ss (fetched
 * from the same hackcal data as the calendar/next-event teaser). Refetches every 60s
 * to pick up newly announced events or roll over once the current one has started,
 * but only while the tab is visible.
 */
export async function initCountdown(): Promise<void> {
  const el = document.getElementById("nextEventCountdown");
  if (!el) return;

  let target: Date | null = null;
  let rendered = "";

  /** Writes only on an actual change - the ticker fires every second, but the text
      is identical whenever the countdown is parked on "Läuft gerade" or similar. */
  const show = (value: string): void => {
    if (rendered === value) return;
    rendered = value;
    el.textContent = value;
  };

  const tick = (): void => {
    if (!target) return;
    const remaining = target.getTime() - Date.now();
    show(remaining <= 0 ? "Läuft gerade" : formatRemaining(remaining));
  };

  const refresh = async (): Promise<void> => {
    try {
      const data = await fetchHackcal();
      const next = findNextEvent(data);
      const start = next ? parseStart(next.dateKey, next.datestr) : null;

      if (!start) {
        target = null;
        show("Kein Termin geplant");
        return;
      }

      target = start;
      tick();
    } catch (err) {
      console.error("Konnte Countdown nicht laden", err);
      if (!target) show("Nicht verfügbar");
    }
  };

  await refresh();

  // A countdown nobody can see doesn't need updating; it is recomputed from the
  // clock on the next visible tick anyway, so nothing drifts.
  setVisibleInterval(tick, TICK_INTERVAL_MS);
  setVisibleInterval(() => void refresh(), REFRESH_INTERVAL_MS);
}
